import { useEffect, useState } from "react";
import { fetchStats } from "@/lib/api";
import { ShieldCheck, Cpu, Headphones, Boxes } from "lucide-react";
import Reveal, { CountUp } from "@/components/Reveal";

const clients = [
  { name: "Vi", src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_led-web-platform/artifacts/4mwec5zs_Logo_of_Vodafone_Idea.png" },
  { name: "Hyundai", src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_led-web-platform/artifacts/91b20n7u_images.jpg" },
  { name: "GE", src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_led-web-platform/artifacts/by3gr3km_General_Electric-Logo.wine.webp" },
  { name: "Ginger", src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_led-web-platform/artifacts/xdaafh86_images.png" },
];

const VALUES = [
  { icon: Cpu, title: "Engineered in-house", text: "We design, spec, and calibrate every panel ourselves — no resold hardware." },
  { icon: ShieldCheck, title: "Built to standard", text: "IP-rated, broadcast-certified, and tested to operate under continuous duty." },
  { icon: Headphones, title: "24/7 operations desk", text: "Live monitoring, content ops, and on-call field engineers around the clock." },
  { icon: Boxes, title: "End-to-end delivery", text: "From site survey to rigging, content, and post-install support — one team." },
];

export const Stats = () => {
  const [stats, setStats] = useState({ installations: 500, square_meters: 18500, years: 12 });

  useEffect(() => { fetchStats().then(setStats).catch(() => {}); }, []);

  return (
    <section id="about" data-testid="stats-section" className="relative py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-4">About Smart Digi</div>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0B1220] leading-[1.1] mb-6">
              Engineered in-house.
            </h2>
            <p className="text-[#4B5563] text-base lg:text-lg leading-relaxed mb-5">
              Smart Digi is a display-systems engineering practice spanning LED and LCD hardware,
              content design, and 24/7 operations. We own the pixel from cabinet to canvas — so
              when your screen goes live, it stays live.
            </p>
            <p className="text-[#6B7280] text-sm leading-relaxed">
              Serving global broadcast, sports, retail, and corporate clients with in-house
              engineering, deployment, and 24/7 operations.
            </p>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-[#E7E5E0] border hairline rounded-2xl overflow-hidden">
            <StatBox
              value={<CountUp end={stats.installations} suffix="+" />}
              label="Installations delivered"
              testid="stat-installations"
              accent
            />
            <StatBox
              value={<><CountUp end={stats.years} /> yrs</>}
              label="In the industry"
              testid="stat-years"
            />
          </div>
        </div>

        <div className="mt-20 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E7E5E0] border hairline rounded-2xl overflow-hidden">
          {VALUES.map(({ icon: Icon, title, text }, idx) => (
            <Reveal key={title} delay={idx * 0.08} className="bg-white p-8">
              <div className="w-10 h-10 rounded-lg bg-[#EAF0F8] flex items-center justify-center mb-5">
                <Icon size={18} className="text-[#00439C]" />
              </div>
              <h3 className="font-display font-bold text-base text-[#0B1220] mb-2.5">{title}</h3>
              <p className="text-[#4B5563] text-sm leading-relaxed">{text}</p>
            </Reveal>
          ))}
        </div>

        <div className="mt-20 pt-12 border-t hairline">
          <div className="text-center text-[11px] uppercase tracking-[0.2em] font-semibold text-[#6B7280] mb-8">
            Trusted by leading brands
          </div>
          <div className="overflow-hidden">
            <div className="flex marquee-track gap-16 lg:gap-24 items-center whitespace-nowrap">
              {[...clients, ...clients].map((c, i) => (
                <img
                  key={i}
                  src={c.src}
                  alt={c.name}
                  className="h-12 lg:h-14 w-auto object-contain opacity-90 hover:opacity-100 transition duration-300 shrink-0"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const StatBox = ({ value, label, testid, accent }) => (
  <div data-testid={testid} className="bg-white p-8 lg:p-10">
    <div className={`font-display font-extrabold text-4xl lg:text-5xl tabular-nums leading-none tracking-tight ${accent ? "text-[#00439C]" : "text-[#0B1220]"}`}>
      {value}
    </div>
    <div className="mt-4 text-sm text-[#4B5563] font-medium">{label}</div>
  </div>
);

export default Stats;
